import {
  Button,
  Field,
  Text,
  TextInput,
  TooltipWrapper,
} from "@zapier/design-system";
import { IconButton } from "@zapier/design-system-beta";
import { useAtom, useSetAtom } from "jotai";
import { canvasAtom, canvasAtomWriteOnly } from "@zapier/canvas-state";

import { useEmitUserInteractionEvents } from "../../hooks/useEmitUserInteractionEvents";
import getBaseUrl from "../../utils/getBaseUrl";

import styles from "./PublicLink.module.css";

type Props = {
  isViewOnlyMode: boolean;
};

export default function PublicLink({ isViewOnlyMode }: Props) {
  const [canvas, updateCanvas] = useAtom(canvasAtom);
  const saveMeta = useSetAtom(canvasAtomWriteOnly);
  const { emitEventOnPublicLinkSharing } = useEmitUserInteractionEvents();

  function handleChangeCanvasIsPublic(is_public: boolean) {
    updateCanvas((cnvs) => ({
      ...cnvs,
      is_public,
    }));
    void saveMeta({
      is_public,
    });
    emitEventOnPublicLinkSharing(is_public);
  }

  function Description() {
    return (
      <div className={styles.descriptionWrapper}>
        <Text type="Body1">
          A public link allows you to share this canvas as <b>view only</b> with
          those who do not have access to your Zapier account.
        </Text>
      </div>
    );
  }

  function CopyToClipboardField() {
    return (
      <Field
        label="Public link"
        renderInput={() => (
          <div
            className={`${styles.copyToClipboardInputRow} ${
              isViewOnlyMode ? styles.copyToClipboardInputRowViewOnly : ""
            }`}
          >
            <TextInput
              isDisabled={true}
              value={`${getBaseUrl()}/app/canvas/public/${canvas.id}`}
              size="medium"
            />
            <TooltipWrapper position="north" content="Copy">
              {() => (
                <IconButton
                  name="actionCopy"
                  onPress={() =>
                    copyTextToClipboard(
                      `${getBaseUrl()}/app/canvas/public/${canvas.id}`,
                    )
                  }
                />
              )}
            </TooltipWrapper>
            {!isViewOnlyMode && (
              <TooltipWrapper
                position="north"
                content="Disable public link sharing"
              >
                {() => (
                  <IconButton
                    name="actionTrash"
                    onPress={() => handleChangeCanvasIsPublic(false)}
                  />
                )}
              </TooltipWrapper>
            )}
          </div>
        )}
      />
    );
  }

  if (isViewOnlyMode) {
    if (!canvas.is_public) {
      return (
        <div className={styles.wrapperViewOnly}>
          <Text type="Body1">
            Request a public link from the canvas owner or an editor.
          </Text>
        </div>
      );
    }

    return (
      <div className={styles.wrapper}>
        <Description />
        <CopyToClipboardField />
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <Description />
      {!canvas.is_public ? (
        <div className={styles.buttonWrapper}>
          <Button
            color="primary"
            size="medium"
            iconBefore="actionLink"
            isFullWidth={true}
            onClick={() => handleChangeCanvasIsPublic(true)}
          >
            Generate public link
          </Button>
        </div>
      ) : (
        <CopyToClipboardField />
      )}
    </div>
  );
}

function copyTextToClipboard(text: string) {
  navigator.clipboard.writeText(text);
}
